import fetch from 'isomorphic-fetch';

import {
  CaptureSpecifier,
  ComparisonSpecifier,
  SnapshotResourceSpecifier,
} from '../chromatic-lib/constants/SnapshotResourceSpecifier';
import { TestStatus } from '../chromatic-lib/constants/TestStatus';

// See comment in preview.js explaining this
let noCredentials = false;
export function setNoCredentials(value: boolean) {
  noCredentials = value;
}
let noFetch = false;
export function setNoFetch(value: boolean) {
  noFetch = value;
}

interface TestComparison {
  browser: string;
  headCapture?: {
    imageUrl?: string;
    domSnapshotUrl?: string;
    errorsJsonUrl?: string;
  };
  diff?: {
    imageUrl?: string;
    focusImageUrl?: string;
  };
}

const fetchImage = (url: string) =>
  new Promise((resolve, reject) => {
    if (noFetch) return resolve(null);

    const img = new window.Image();
    img.src = url;
    img.onload = resolve;
    img.onerror = reject;
  });

const fetchUrl = async (url: string, { ignoreResponse = false } = {}) => {
  if (!url || noFetch) return null;
  try {
    const res = await fetch(url, noCredentials ? {} : { credentials: 'include', mode: 'cors' });
    if (ignoreResponse) {
      res.text(); // Unused but not reading the body is a memory leak
      return null;
    } else {
      return await res.text();
    }
  } catch (e) {
    console.warn(e);
  }
};

async function fetchAll(
  test: { comparisons: TestComparison[] },
  specifier: SnapshotResourceSpecifier,
  options?: { ignoreResponse?: boolean }
) {
  const fetcher = {
    [CaptureSpecifier.CaptureImage]: (comparison: TestComparison) =>
      fetchImage(comparison.headCapture.imageUrl),

    [CaptureSpecifier.CaptureDOM]: (comparison: TestComparison) =>
      fetchUrl(comparison.headCapture.domSnapshotUrl, options),

    [CaptureSpecifier.ErrorsJSON]: (comparison: TestComparison) =>
      fetchUrl(comparison.headCapture.errorsJsonUrl, options),

    [ComparisonSpecifier.ComparisonImage]: (comparison: TestComparison) =>
      fetchImage(comparison.diff.imageUrl),

    [ComparisonSpecifier.FocusImage]: (comparison: TestComparison) =>
      fetchImage(comparison.diff.focusImageUrl),
  }[specifier];

  return Object.fromEntries(
    await Promise.all(test.comparisons.map(async (cmp) => [cmp.browser, await fetcher(cmp)]))
  );
}

export default async function fetchTestMetadata(
  test: {
    comparisons: TestComparison[];
    status: TestStatus;
    acceptedBaseline?: { comparisons: TestComparison[] };
  },
  { noResults: ignoreResponse = false } = {}
) {
  const { comparisons, status, acceptedBaseline } = test;
  if (status === TestStatus.BROKEN) {
    return {
      captureErrors: await fetchAll(test, CaptureSpecifier.ErrorsJSON, { ignoreResponse }),
    };
  }

  // We don't use the result of these, but just load them for caching purposes
  fetchAll(test, CaptureSpecifier.CaptureImage, { ignoreResponse: true });
  if (acceptedBaseline) {
    fetchAll(acceptedBaseline, CaptureSpecifier.CaptureImage, { ignoreResponse: true });
    fetchAll(test, ComparisonSpecifier.ComparisonImage, { ignoreResponse: true });
    fetchAll(test, ComparisonSpecifier.FocusImage, { ignoreResponse: true });
  }

  // We just grab the DOM for the *first* comparison
  //   (as we don't actually have a way of showing more than one diff)
  const [captureDom, acceptedBaselineCaptureDom] = await Promise.all([
    fetchUrl(comparisons[0]?.headCapture?.domSnapshotUrl, { ignoreResponse }),
    fetchUrl(acceptedBaseline?.comparisons[0]?.headCapture?.domSnapshotUrl, { ignoreResponse }),
  ]);

  return { captureDom, acceptedBaselineCaptureDom };
}
